import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Plus, Pencil, Save, X, Trash2, RotateCcw, Lock, Check, ArrowUp, ArrowDown, Building2, Briefcase, Shield } from "lucide-react";
import { toast } from "sonner";
import { deptAvatarClass } from "@/lib/employees";
import { useIdentity } from "@/lib/identity";
import EmployeePicker from "@/components/EmployeePicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function newId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

// ----- Pure helpers to update nested matrix objects (return new objects) -----
const update = {
  cellEmpAdd(matrix, locator, employeeId) {
    return mapCell(matrix, locator, (c) => ({ ...c, employee_ids: Array.from(new Set([...(c.employee_ids || []), employeeId])) }));
  },
  cellEmpRemove(matrix, locator, employeeId) {
    return mapCell(matrix, locator, (c) => ({ ...c, employee_ids: (c.employee_ids || []).filter((x) => x !== employeeId) }));
  },
  cellRename(matrix, locator, name) {
    return mapCell(matrix, locator, (c) => ({ ...c, name }));
  },
  cellRemove(matrix, locator) {
    return mapCells(matrix, locator.section, locator.deptId, (cells) => cells.filter((c) => c.id !== locator.cellId));
  },
  cellSetScope(matrix, locator, scope) {
    return mapCell(matrix, locator, (c) => ({ ...c, bu_scope: scope }));
  },
  cellAdd(matrix, section, deptId, scope = "solutii") {
    return mapCells(matrix, section, deptId, (cells) => [...cells, { id: newId("cell"), name: "Celulă nouă", employee_ids: [], external_names: [], bu_scope: scope }]);
  },
  productAdd(matrix, buId) {
    return {
      ...matrix,
      business_units: matrix.business_units.map((bu) =>
        bu.id === buId ? { ...bu, products: [...(bu.products || []), { id: newId("prod"), name: "Produs nou", leader_ids: [] }] } : bu
      ),
    };
  },
  productRemove(matrix, buId, prodId) {
    return {
      ...matrix,
      business_units: matrix.business_units.map((bu) =>
        bu.id === buId ? { ...bu, products: (bu.products || []).filter((p) => p.id !== prodId) } : bu
      ),
    };
  },
  productRename(matrix, buId, prodId, name) {
    return {
      ...matrix,
      business_units: matrix.business_units.map((bu) =>
        bu.id === buId ? { ...bu, products: (bu.products || []).map((p) => (p.id === prodId ? { ...p, name } : p)) } : bu
      ),
    };
  },
  buRename(matrix, buId, name) {
    return { ...matrix, business_units: matrix.business_units.map((bu) => (bu.id === buId ? { ...bu, name } : bu)) };
  },
  buAdd(matrix) {
    return {
      ...matrix,
      business_units: [
        ...matrix.business_units,
        { id: newId("bu"), name: "Business Unit nou", leader_ids: [], products: [] },
      ],
    };
  },
  buRemove(matrix, buId) {
    return { ...matrix, business_units: matrix.business_units.filter((bu) => bu.id !== buId) };
  },
  buMove(matrix, buId, direction) {
    const list = [...matrix.business_units];
    const idx = list.findIndex((b) => b.id === buId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return matrix;
    [list[idx], list[target]] = [list[target], list[idx]];
    return { ...matrix, business_units: list };
  },
  deptRename(matrix, deptId, name) {
    return { ...matrix, departments: matrix.departments.map((d) => (d.id === deptId ? { ...d, name } : d)) };
  },
  deptAdd(matrix) {
    return {
      ...matrix,
      departments: [
        ...matrix.departments,
        { id: newId("dept"), name: "Departament nou", leader_ids: [], cells: [] },
      ],
    };
  },
  deptRemove(matrix, deptId) {
    return { ...matrix, departments: matrix.departments.filter((d) => d.id !== deptId) };
  },
  deptMove(matrix, deptId, direction) {
    const list = [...matrix.departments];
    const idx = list.findIndex((d) => d.id === deptId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return matrix;
    [list[idx], list[target]] = [list[target], list[idx]];
    return { ...matrix, departments: list };
  },
};

function mapCells(matrix, section, deptId, fn) {
  if (section === "department") {
    return { ...matrix, departments: matrix.departments.map((d) => (d.id === deptId ? { ...d, cells: fn(d.cells || []) } : d)) };
  }
  if (section === "governance") {
    return { ...matrix, governance: { ...matrix.governance, cells: fn(matrix.governance.cells || []) } };
  }
  return matrix;
}

function mapCell(matrix, locator, fn) {
  return mapCells(matrix, locator.section, locator.deptId, (cells) =>
    cells.map((c) => (c.id === locator.cellId ? fn(c) : c))
  );
}

// ---------- Sub-components ----------
function InlineEditableTitle({ value, onSave, className = "", testid }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  if (!editing) {
    return (
      <button
        onClick={() => setEditing(true)}
        data-testid={testid}
        className={`text-left hover:bg-white/20 rounded px-1 -mx-1 transition-colors ${className}`}
        title="Click pentru a redenumi"
      >
        {value}
      </button>
    );
  }
  return (
    <div className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); onSave(draft); setEditing(false); }
          else if (e.key === "Escape") { setDraft(value); setEditing(false); }
        }}
        className="bg-white text-zinc-900 px-1.5 py-0.5 rounded text-sm border border-zinc-300 outline-none"
        style={{ minWidth: 80 }}
      />
      <button onClick={() => { onSave(draft); setEditing(false); }} className="text-emerald-300 hover:text-white">
        <Check className="h-3.5 w-3.5" />
      </button>
      <button onClick={() => { setDraft(value); setEditing(false); }} className="text-zinc-300 hover:text-white">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

function PersonTag({ emp, onRemove, editable }) {
  return (
    <span className="inline-flex items-center gap-1 pl-1 pr-1.5 py-0.5 rounded-full bg-white/95 dark:bg-zinc-100 text-[11px] text-zinc-800 shadow-sm">
      <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold ${deptAvatarClass(emp.department)}`}>
        {emp.initials}
      </span>
      <span className="font-medium">{emp.name.split(" ").slice(0, 2).join(" ")}</span>
      {editable && (
        <button onClick={onRemove} className="ml-0.5 inline-flex items-center justify-center h-3.5 w-3.5 rounded-full text-zinc-500 hover:bg-red-500 hover:text-white transition-colors">
          <X className="h-2.5 w-2.5" />
        </button>
      )}
    </span>
  );
}

function ExternalTag({ name }) {
  return (
    <span className="inline-flex items-center gap-1 pl-1.5 pr-1.5 py-0.5 rounded-full bg-zinc-200/90 dark:bg-zinc-300 text-[11px] text-zinc-700 italic">
      {name}
    </span>
  );
}

const DEFAULT_MATRIX_DESC = "Structura matricială este un model organizațional în care responsabilitatea și raportarea se exercită pe două axe complementare — cea funcțională (ariile de specialitate) și cea de business unit / proiect — permițând alocarea dinamică a competențelor către obiectivele cu prioritate strategică, fără redundanță de roluri.\n\nAcest model aduce beneficii concrete: valorificarea optimă a expertizei specializate prin contribuția la multiple inițiative simultan, agilitate decizională în raport cu prioritățile de business, transfer accelerat de know-how între echipe și o aliniere mai puternică între competențele funcționale și obiectivele comerciale.";

// BU scope config for cells (Soluții / Servicii / Transversal)
const BU_SCOPES = {
  solutii: { label: "BUD Soluții", short: "Soluții", dot: "bg-sky-500" },
  servicii: { label: "BUD Servicii", short: "Servicii", dot: "bg-teal-500" },
  transversal: { label: "Transversal", short: "Transv.", dot: "bg-violet-500" },
};

function ScopeBadge({ scope }) {
  const cfg = BU_SCOPES[scope];
  if (!cfg) return null;
  return (
    <span
      data-testid={`cell-scope-badge-${scope}`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-white/95 dark:bg-white text-[9px] font-semibold uppercase tracking-wide text-zinc-700"
    >
      <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function Cell({ cell, employees, onOpenProfile, editable, onAddEmp, onRemoveEmp, onRename, onRemoveCell, onSetScope, showScope }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const addBtnRef = useRef(null);
  return (
    <div
      data-testid={`matrix-cell-${cell.id}`}
      className="relative p-3 rounded-lg bg-teal-700/95 dark:bg-teal-800 text-white border border-teal-800/40 shadow-sm"
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          {editable ? (
            <InlineEditableTitle
              testid={`cell-title-${cell.id}`}
              value={cell.name}
              onSave={(v) => onRename(v)}
              className="font-semibold text-[12px] uppercase tracking-wide block"
            />
          ) : (
            <div className="font-semibold text-[12px] uppercase tracking-wide leading-tight">{cell.name}</div>
          )}
        </div>
        {editable && (
          <button
            data-testid={`cell-delete-${cell.id}`}
            onClick={() => { if (window.confirm(`Ștergi celula „${cell.name}"?`)) onRemoveCell(); }}
            className="opacity-60 hover:opacity-100 inline-flex items-center justify-center h-5 w-5 rounded hover:bg-red-500/50"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* BU scope badge (view) — doar pentru transversal (coloanele indică restul) */}
      {showScope && !editable && cell.bu_scope === "transversal" && (
        <div className="mt-1.5">
          <ScopeBadge scope={cell.bu_scope} />
        </div>
      )}

      {/* BU scope picker (edit) */}
      {showScope && editable && (
        <div className="mt-2">
          <div className="text-[8px] uppercase tracking-[0.15em] opacity-70 mb-1">Scop (Business Unit)</div>
          <div className="inline-flex items-center gap-1 flex-wrap">
            {Object.entries(BU_SCOPES).map(([k, c]) => {
              const selected = (cell.bu_scope || "solutii") === k;
              return (
                <button
                  key={k}
                  data-testid={`cell-scope-${cell.id}-${k}`}
                  onClick={() => onSetScope(k)}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                    selected ? "bg-white text-zinc-900" : "bg-white/15 text-white hover:bg-white/30"
                  }`}
                >
                  <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                  {c.short}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-2 flex flex-wrap gap-1">
        {(cell.employee_ids || []).map((id) => {
          const emp = employees.find((e) => e.id === id);
          if (!emp) return null;
          if (editable) {
            return (
              <span key={id} data-testid={`cell-emp-${cell.id}-${id}`} className="inline-block">
                <PersonTag emp={emp} editable={true} onRemove={() => onRemoveEmp(id)} />
              </span>
            );
          }
          return (
            <button
              key={id}
              onClick={() => onOpenProfile?.(id)}
              data-testid={`cell-emp-${cell.id}-${id}`}
              className="focus:outline-none"
            >
              <PersonTag emp={emp} editable={false} />
            </button>
          );
        })}
        {(cell.external_names || []).map((nm) => <ExternalTag key={nm} name={nm} />)}
        {editable && (
          <button
            ref={addBtnRef}
            data-testid={`cell-add-emp-${cell.id}`}
            onClick={() => setPickerOpen((v) => !v)}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 hover:bg-white/30 text-[10px] uppercase tracking-wider transition-colors"
          >
            <Plus className="h-3 w-3" /> persoană
          </button>
        )}
      </div>

      {pickerOpen && (
        <EmployeePicker
          employees={employees}
          selectedIds={cell.employee_ids || []}
          anchorLabel={cell.name}
          anchorRef={addBtnRef}
          onAdd={(id) => { onAddEmp(id); setPickerOpen(false); }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

// ---------- Main view ----------
export default function MatrixOrgChart({ employees, onOpenProfile }) {
  const { me } = useIdentity();
  const isAdmin = me && me.is_admin;
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [originalMatrix, setOriginalMatrix] = useState(null);

  const fetchMatrix = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/matrix`);
      setMatrix(r.data);
      setOriginalMatrix(r.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMatrix(); }, [fetchMatrix]);

  const startEdit = () => { setOriginalMatrix(matrix); setEditing(true); };
  const cancelEdit = () => { setMatrix(originalMatrix); setEditing(false); };

  const saveAll = async () => {
    setSaving(true);
    try {
      const { _id, ...body } = matrix;
      const r = await axios.put(`${API}/matrix`, body);
      setMatrix(r.data);
      setOriginalMatrix(r.data);
      setEditing(false);
      toast.success("Structură salvată");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Resetezi structura la varianta inițială din imagine? Modificările tale vor fi pierdute.")) return;
    try {
      const r = await axios.post(`${API}/matrix/reset`);
      setMatrix(r.data);
      setOriginalMatrix(r.data);
      setEditing(false);
      toast.success("Structură resetată");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la reset");
    }
  };

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-zinc-500 dark:text-zinc-400">Se încarcă matricea...</div>;
  if (error) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-red-600">Eroare: {error}</div>;
  if (!matrix) return null;

  const buCells = (buId) => {
    const bu = matrix.business_units.find((b) => b.id === buId);
    return bu ? (bu.products || []) : [];
  };

  return (
    <div data-testid="matrix-view" className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
      <div className="mb-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400 font-medium">
              Structură organizațională matricială
            </p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900 dark:text-zinc-50">
              {matrix.title}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {!editing && !isAdmin && (
              <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                <Lock className="h-3.5 w-3.5" /> Doar vizualizare
              </span>
            )}
            {!editing && isAdmin && (
              <button
                data-testid="matrix-edit-btn"
                onClick={startEdit}
                className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Editează
              </button>
            )}
            {editing && (
              <>
                <button
                  data-testid="matrix-reset-btn"
                  onClick={resetToDefault}
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-sm font-medium text-zinc-500 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Reset
                </button>
                <button
                  data-testid="matrix-cancel-btn"
                  onClick={cancelEdit}
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                >
                  <X className="h-3.5 w-3.5" /> Anulează
                </button>
                <button
                  data-testid="matrix-save-btn"
                  onClick={saveAll}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  <Save className="h-3.5 w-3.5" /> {saving ? "Se salvează..." : "Salvează"}
                </button>
              </>
            )}
          </div>
        </div>
        <div className="mt-4 text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
          {editing ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 block mb-1">
                Descriere structură
              </label>
              <textarea
                data-testid="matrix-description-input"
                value={matrix.description ?? DEFAULT_MATRIX_DESC}
                onChange={(e) => setMatrix({ ...matrix, description: e.target.value })}
                rows={6}
                className="w-full px-3 py-2 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100 leading-relaxed"
              />
            </div>
          ) : (
            <p data-testid="matrix-description" className="whitespace-pre-line max-w-none">
              {matrix.description || DEFAULT_MATRIX_DESC}
            </p>
          )}
        </div>
      </div>

      {/* Director General — top banner */}
      <div className="mb-3 rounded-lg p-4 bg-violet-900 text-white text-center shadow-sm">
        <div className="text-[10px] uppercase tracking-[0.3em] opacity-80">{matrix.director_general?.name || "Director General"}</div>
        <div className="text-lg font-bold tracking-tight">
          {(matrix.director_general?.employee_ids || []).map((id) => {
            const e = employees.find((x) => x.id === id);
            return e ? e.name : null;
          }).filter(Boolean).join(" · ") || "—"}
        </div>
      </div>

      {/* Business Units strip */}
      <div className="mb-1 rounded-md p-2 bg-violet-800 text-white text-center text-[11px] uppercase tracking-[0.3em]">Business Units</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
        {matrix.business_units.map((bu, buIdx) => (
          <div key={bu.id} data-testid={`bu-${bu.id}`} className="rounded-lg overflow-hidden border border-sky-700/40 shadow-sm">
            <div className="px-4 py-3 bg-sky-600 text-white">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Business Unit</div>
                {editing && (
                  <div className="flex items-center gap-1">
                    <button
                      data-testid={`bu-move-up-${bu.id}`}
                      onClick={() => setMatrix(update.buMove(matrix, bu.id, -1))}
                      disabled={buIdx === 0}
                      className="opacity-80 hover:opacity-100 disabled:opacity-30 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-white/20"
                      title="Mută în sus"
                    >
                      <ArrowUp className="h-3 w-3" />
                    </button>
                    <button
                      data-testid={`bu-move-down-${bu.id}`}
                      onClick={() => setMatrix(update.buMove(matrix, bu.id, 1))}
                      disabled={buIdx === matrix.business_units.length - 1}
                      className="opacity-80 hover:opacity-100 disabled:opacity-30 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-white/20"
                      title="Mută în jos"
                    >
                      <ArrowDown className="h-3 w-3" />
                    </button>
                    <button
                      data-testid={`bu-delete-${bu.id}`}
                      onClick={() => { if (window.confirm(`Ștergi Business Unit „${bu.name}"?`)) setMatrix(update.buRemove(matrix, bu.id)); }}
                      className="opacity-80 hover:opacity-100 inline-flex h-5 w-5 items-center justify-center rounded hover:bg-red-500/60"
                      title="Șterge BU"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )}
              </div>
              {editing ? (
                <InlineEditableTitle
                  testid={`bu-title-${bu.id}`}
                  value={bu.name}
                  onSave={(v) => setMatrix(update.buRename(matrix, bu.id, v))}
                  className="text-lg font-bold tracking-tight block"
                />
              ) : (
                <div className="text-lg font-bold tracking-tight">{bu.name}</div>
              )}
              <div className="mt-0.5 text-xs opacity-90">
                {(bu.leader_ids || []).map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean).join(" · ")}
              </div>
            </div>
            <div className="bg-sky-500/90 p-2 flex flex-wrap gap-2">
              {(bu.products || []).map((p) => (
                <div key={p.id} className="relative inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-400/90 text-white text-xs">
                  {editing ? (
                    <InlineEditableTitle
                      value={p.name}
                      onSave={(v) => setMatrix(update.productRename(matrix, bu.id, p.id, v))}
                      className="font-semibold"
                    />
                  ) : (
                    <span className="font-semibold">{p.name}</span>
                  )}
                  <span className="opacity-90">
                    {(p.leader_ids || []).map((id) => employees.find((e) => e.id === id)?.name?.split(" ")[0]).filter(Boolean).join(", ")}
                  </span>
                  {editing && (
                    <button
                      onClick={() => setMatrix(update.productRemove(matrix, bu.id, p.id))}
                      className="opacity-70 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
              {editing && (
                <button
                  data-testid={`bu-add-product-${bu.id}`}
                  onClick={() => setMatrix(update.productAdd(matrix, bu.id))}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 hover:bg-white/40 text-[10px] uppercase tracking-wider text-white transition-colors"
                >
                  <Plus className="h-3 w-3" /> produs
                </button>
              )}
            </div>
          </div>
        ))}
        {editing && (
          <button
            data-testid="bu-add-btn"
            onClick={() => setMatrix(update.buAdd(matrix))}
            className="rounded-lg border-2 border-dashed border-sky-300 dark:border-sky-700 hover:border-sky-500 dark:hover:border-sky-500 hover:bg-sky-50 dark:hover:bg-sky-950/30 flex items-center justify-center gap-2 p-6 transition-colors text-sky-700 dark:text-sky-400 font-medium"
          >
            <Plus className="h-5 w-5" />
            <span>Adaugă Business Unit</span>
          </button>
        )}
      </div>

      {/* Departments */}
      <div className="space-y-3">
        {matrix.departments.map((dept, deptIdx) => {
          const deptCells = dept.cells || [];
          const cellSolutii = deptCells.filter((c) => (c.bu_scope || "solutii") === "solutii");
          const cellServicii = deptCells.filter((c) => c.bu_scope === "servicii");
          const cellTransversal = deptCells.filter((c) => c.bu_scope === "transversal");
          const renderDeptCell = (cell) => (
            <Cell
              key={cell.id}
              cell={cell}
              employees={employees}
              onOpenProfile={onOpenProfile}
              editable={editing}
              onAddEmp={(id) => setMatrix(update.cellEmpAdd(matrix, { section: "department", deptId: dept.id, cellId: cell.id }, id))}
              onRemoveEmp={(id) => setMatrix(update.cellEmpRemove(matrix, { section: "department", deptId: dept.id, cellId: cell.id }, id))}
              onRename={(v) => setMatrix(update.cellRename(matrix, { section: "department", deptId: dept.id, cellId: cell.id }, v))}
              onRemoveCell={() => setMatrix(update.cellRemove(matrix, { section: "department", deptId: dept.id, cellId: cell.id }))}
              onSetScope={(scope) => setMatrix(update.cellSetScope(matrix, { section: "department", deptId: dept.id, cellId: cell.id }, scope))}
              showScope={true}
            />
          );
          const addCellBtn = (scope, label, dot) => (
            <button
              data-testid={`dept-add-cell-${dept.id}-${scope}`}
              onClick={() => setMatrix(update.cellAdd(matrix, "department", dept.id, scope))}
              className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md border border-dashed border-teal-400 dark:border-teal-700 text-teal-700 dark:text-teal-300 text-[11px] font-medium hover:bg-teal-100 dark:hover:bg-teal-900/30 transition-colors"
            >
              <span className={`w-2 h-2 rounded-full ${dot}`} /> <Plus className="h-3 w-3" /> {label}
            </button>
          );
          return (
          <section key={dept.id} data-testid={`dept-${dept.id}`} className="rounded-lg overflow-hidden border border-teal-700/40 shadow-sm">
            <header className="px-4 py-2.5 bg-teal-900 text-white flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Departament</div>
                {editing ? (
                  <InlineEditableTitle
                    testid={`dept-title-${dept.id}`}
                    value={dept.name}
                    onSave={(v) => setMatrix(update.deptRename(matrix, dept.id, v))}
                    className="text-base font-bold tracking-tight block"
                  />
                ) : (
                  <div className="text-base font-bold tracking-tight">{dept.name}</div>
                )}
                <div className="text-xs opacity-80">
                  {(dept.leader_ids || []).map((id) => employees.find((e) => e.id === id)?.name).filter(Boolean).join(" · ")}
                </div>
              </div>
              {editing && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    data-testid={`dept-move-up-${dept.id}`}
                    onClick={() => setMatrix(update.deptMove(matrix, dept.id, -1))}
                    disabled={deptIdx === 0}
                    className="opacity-80 hover:opacity-100 disabled:opacity-30 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-white/20"
                    title="Mută în sus"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`dept-move-down-${dept.id}`}
                    onClick={() => setMatrix(update.deptMove(matrix, dept.id, 1))}
                    disabled={deptIdx === matrix.departments.length - 1}
                    className="opacity-80 hover:opacity-100 disabled:opacity-30 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-white/20"
                    title="Mută în jos"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button
                    data-testid={`dept-delete-${dept.id}`}
                    onClick={() => { if (window.confirm(`Ștergi departamentul „${dept.name}" cu toate celulele?`)) setMatrix(update.deptRemove(matrix, dept.id)); }}
                    className="opacity-80 hover:opacity-100 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-red-500/60"
                    title="Șterge departament"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </header>
            <div className="bg-teal-50 dark:bg-teal-950/30 p-3 space-y-2.5">
              {/* Transversal — full width (sub ambele BU) */}
              {(cellTransversal.length > 0 || editing) && (
                <div className="space-y-2">
                  {cellTransversal.map(renderDeptCell)}
                  {editing && addCellBtn("transversal", "Celulă transversală", "bg-violet-500")}
                </div>
              )}
              {/* BU columns — aliniate cu Business Unit-urile */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5">
                <div className="space-y-2" data-testid={`dept-col-solutii-${dept.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-sky-700 dark:text-sky-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-sky-500" /> BUD Soluții
                  </div>
                  {cellSolutii.map(renderDeptCell)}
                  {editing && addCellBtn("solutii", "Celulă Soluții", "bg-sky-500")}
                </div>
                <div className="space-y-2" data-testid={`dept-col-servicii-${dept.id}`}>
                  <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-teal-700 dark:text-teal-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-teal-500" /> BUD Servicii
                  </div>
                  {cellServicii.map(renderDeptCell)}
                  {editing && addCellBtn("servicii", "Celulă Servicii", "bg-teal-500")}
                </div>
              </div>
            </div>
          </section>
          );
        })}
        {editing && (
          <button
            data-testid="dept-add-btn"
            onClick={() => setMatrix(update.deptAdd(matrix))}
            className="w-full rounded-lg border-2 border-dashed border-teal-300 dark:border-teal-700 hover:border-teal-500 dark:hover:border-teal-500 hover:bg-teal-50 dark:hover:bg-teal-950/30 flex items-center justify-center gap-2 p-5 transition-colors text-teal-700 dark:text-teal-400 font-medium"
          >
            <Plus className="h-5 w-5" />
            <span>Adaugă departament</span>
          </button>
        )}
      </div>

      {/* Governance */}
      <section data-testid="governance" className="mt-3 rounded-lg overflow-hidden border border-violet-700/40 shadow-sm">
        <header className="px-4 py-2.5 bg-violet-900 text-white flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Funcțiuni suport</div>
            <div className="text-base font-bold tracking-tight">{matrix.governance?.name || "Guvernanță Corporativă"}</div>
          </div>
          {editing && (
            <button
              data-testid="gov-add-cell"
              onClick={() => setMatrix(update.cellAdd(matrix, "governance"))}
              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/20 hover:bg-white/40 text-[10px] uppercase tracking-wider transition-colors"
            >
              <Plus className="h-3 w-3" /> celulă
            </button>
          )}
        </header>
        <div className="bg-violet-50 dark:bg-violet-950/30 p-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {(matrix.governance?.cells || []).map((cell) => (
              <Cell
                key={cell.id}
                cell={cell}
                employees={employees}
                onOpenProfile={onOpenProfile}
                editable={editing}
                onAddEmp={(id) => setMatrix(update.cellEmpAdd(matrix, { section: "governance", cellId: cell.id }, id))}
                onRemoveEmp={(id) => setMatrix(update.cellEmpRemove(matrix, { section: "governance", cellId: cell.id }, id))}
                onRename={(v) => setMatrix(update.cellRename(matrix, { section: "governance", cellId: cell.id }, v))}
                onRemoveCell={() => setMatrix(update.cellRemove(matrix, { section: "governance", cellId: cell.id }))}
                showScope={false}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
